// src/app/features/legal-information/legal-information.component.ts


import { Component, ElementRef, HostListener, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { Inject } from '@angular/core';
import { PreReadService } from '../../core/services/pre-read.service';
import { AuthService } from '../../core/services/auth.service';
import { displayIssueTitle, displayIssueType, hasActiveSharedAccess, normalizePreReadStatus, PERSONAL_DOC_INDEX, PreReadListItem } from '../../core/models/pre-read.model';
import { LegalInformationQuestionnaireComponent } from './questionnaire/legal-information-questionnaire/legal-information-questionnaire.component';

import { LegalInformationDeleteConfirmationDialogComponent } from './legal-information-delete-confirmation-dialog/legal-information-delete-confirmation-dialog.component';

import { ShareLegalInformationDialogComponent } from './share-legal-information-dialog/share-legal-information-dialog.component';

interface LegalInformationDialogData {
  preReadId?: string;
  readOnly?: boolean;
  legalFinanceCategoryId?: string | null;
}

/** Same personal-document category context used by the Documents module. */

@Component({
  selector: 'app-legal-information',
  templateUrl: './legal-information.component.html',
  styleUrls: ['./legal-information.component.css'],
  standalone: false
})
export class LegalInformationComponent implements OnInit {

  entries: PreReadListItem[] = [];
  filteredEntries: PreReadListItem[] = [];
  loading = false;
  /** Guards the "Create New" button against duplicate clicks while the category lookup runs (Part 23/26). */
  creatingNew = false;

  searchQuery = '';
  statusFilter: 'All' | 'Draft' | 'Completed' = 'All';
  /** Same 3 values the filter already used — just enumerated for the custom dropdown's *ngFor. */
  statusOptions: Array<'All' | 'Draft' | 'Completed'> = ['All', 'Draft', 'Completed'];
  /** UI-only open/closed state for the custom status dropdown panel (Part: dropdown UI fix). */
  statusDropdownOpen = false;

  // ---------- Pagination (mirrors the Documents module's custom pagination — Part 25) ----------
  currentPage = 0;
  pageSize = 10;

  constructor(
    @Inject(PreReadService) private preReadService: PreReadService,
    private auth: AuthService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private elRef: ElementRef
  ) { }

  ngOnInit(): void {
    this.load();
  }


  displayIssueTitle(entry: PreReadListItem): string {
    return displayIssueTitle(entry);
  }

  displayIssueType(entry: PreReadListItem): string {
    return displayIssueType(entry);
  }

 
  formatUpdatedAt(value: string | undefined | null): string {
    if (!value) { return '—'; }

    const parsed = this.parseApiTimestampAsUtc(value);
    if (!parsed) {
      // Not a parseable date — assume it's already a display-ready string.
      return value;
    }

    const formatted = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Kolkata',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(parsed);

    return `${formatted}`;
  }

 
  private parseApiTimestampAsUtc(value: string): Date | null {
    const trimmed = value.trim();
    if (!trimmed) { return null; }

    if (/^\d+$/.test(trimmed)) {
      const epoch = new Date(Number(trimmed));
      return isNaN(epoch.getTime()) ? null : epoch;
    }

    const hasTimezoneDesignator = /Z$|[+-]\d{2}:?\d{2}$/.test(trimmed);
    if (hasTimezoneDesignator) {
      const withTz = new Date(trimmed);
      return isNaN(withTz.getTime()) ? null : withTz;
    }

    const isoLike = trimmed.replace(' ', 'T');
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d+)?$/.test(isoLike)) {
      const asUtc = new Date(isoLike + 'Z');
      return isNaN(asUtc.getTime()) ? null : asUtc;
    }

    const fallback = new Date(trimmed);
    return isNaN(fallback.getTime()) ? null : fallback;
  }

  load(): void {
    this.loading = true;
    this.preReadService.list().pipe(
      finalize(() => this.loading = false)
    ).subscribe({
      next: (res) => {
        this.entries = (res || []).map(e => ({ ...e, status: normalizePreReadStatus(e.status as any) }));
        this.applyFilters();
      },
      error: () => this.toastr.error('Could not load your Legal Information entries.')
    });
  }

  applyFilters(): void {
    const q = this.searchQuery.trim().toLowerCase();
    this.filteredEntries = this.entries.filter(e => {
      const matchesStatus = this.statusFilter === 'All' || e.status === this.statusFilter;
      const matchesQuery = !q || (e.issue_title || '').toLowerCase().includes(q);
      return matchesStatus && matchesQuery;
    });
    this.currentPage = 0;
  }

  onSearchChange(value: string): void {
    this.searchQuery = value;
    this.applyFilters();
  }

  onStatusFilterChange(value: 'All' | 'Draft' | 'Completed'): void {
    this.statusFilter = value;
    this.applyFilters();
  }

  // ---------- Custom status dropdown (UI-only — delegates to onStatusFilterChange above) ----------

  toggleStatusDropdown(): void {
    this.statusDropdownOpen = !this.statusDropdownOpen;
  }

  selectStatus(value: 'All' | 'Draft' | 'Completed'): void {
    this.statusDropdownOpen = false;
    if (value === this.statusFilter) { return; }
    this.onStatusFilterChange(value);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClickForStatusDropdown(event: MouseEvent): void {
    if (this.statusDropdownOpen && !this.elRef.nativeElement.contains(event.target)) {
      this.statusDropdownOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeForStatusDropdown(): void {
    this.statusDropdownOpen = false;
  }

  get paginatedEntries(): PreReadListItem[] {
    const start = this.getStartIndex();
    return this.filteredEntries.slice(start, start + this.pageSize);
  }

  /**
   * Resolves the legalfinance category id once, up front, when "Create New" is clicked
   * (Part 10/26) so the Supporting Documents step doesn't have to look it up on every step.
   * If the lookup fails, the Questionnaire dialog still opens — it will retry the lookup
   * itself only if/when the user actually reaches the documents step (see the questionnaire
   * component's resolveLegalFinanceCategory fallback).
   */
  createNew(): void {
    if (this.creatingNew) { return; }
    this.creatingNew = true;
    this.auth.get_tags(PERSONAL_DOC_INDEX).pipe(
      finalize(() => this.creatingNew = false)
    ).subscribe({
      next: (res: any) => {
        const categories = res?.data?.categories || [];
        const match = categories.find((c: any) => (c.tagname || '').toLowerCase() === 'legalfinance');
        this.openQuestionnaire({ legalFinanceCategoryId: match?.id || null });
      },
      error: () => this.openQuestionnaire({ legalFinanceCategoryId: null })
    });
  }

  viewSample(): void {
    this.toastr.info('A sample walkthrough is coming soon. Tap "Create New" to get started.');
  }

  continue(entry: PreReadListItem): void {
    this.openQuestionnaire({ preReadId: entry.pre_read_id });
  }

  view(entry: PreReadListItem): void {
    this.openQuestionnaire({ preReadId: entry.pre_read_id, readOnly: true });
  }

  edit(entry: PreReadListItem): void {
    this.openQuestionnaire({ preReadId: entry.pre_read_id });
  }

  private openQuestionnaire(data: LegalInformationDialogData): void {
    const ref = this.dialog.open(LegalInformationQuestionnaireComponent, {
      data,
      width: '900px',
      maxWidth: '92vw',
      panelClass: 'li-dialog-panel',
      autoFocus: false,
      disableClose: true
    });

    ref.afterClosed().subscribe((result) => {
      if (result === 'refresh') {
        this.load();
      }
    });
  }

  delete(entry: PreReadListItem): void {
    // Shared-lawyer detection uses the row's own status/shared_with fields —
    // both already returned by GET /consumer/v3/prereads — so no extra
    // lookup call is needed just to pick the confirmation copy. This is
    // deliberately independent of sharedWithCache/getSharedWith (used only
    // for the list's "Shared With" column), since that cache is populated
    // asynchronously per row and may not be ready yet when Delete is clicked.
    const hasSharedLawyers = hasActiveSharedAccess(entry);

    const ref = this.dialog.open(LegalInformationDeleteConfirmationDialogComponent, {
      data: {
        preReadId: entry.pre_read_id,
        hasSharedLawyers
      },
      width: '480px',
      maxWidth: '92vw',
      panelClass: 'li-delete-dialog-panel',
      disableClose: true,
      autoFocus: false
    });

    // The dialog itself performs the delete call and only closes with
    // `true` once the deletion has actually succeeded, so this callback
    // only ever fires for a real, confirmed deletion.
    ref.afterClosed().subscribe((deleted: boolean) => {
      if (!deleted) { return; }
      this.toastr.success('Legal information deleted.');
      this.load();
    });
  }

  share(entry: PreReadListItem): void {
    
    this.preReadService.getById(entry.pre_read_id).pipe(
      map((full: any) => full?.answers?.incidentDescription || ''),
      catchError(() => of(''))
    ).subscribe((description) => this.openShareDialog(entry, description));
  }

  private openShareDialog(entry: PreReadListItem, description: string): void {
    const ref = this.dialog.open(ShareLegalInformationDialogComponent, {
      data: {
        document: {
          id: entry.pre_read_id,
          name: this.displayIssueTitle(entry),
          title: this.displayIssueTitle(entry),
          description,
          issue_type: (entry as any).issue_type,
          status: entry.status,
          updated_at: entry.updated_at
        }
      },
      width: '700px',
      maxWidth: '92vw',
      panelClass: 'li-share-dialog-panel',
      disableClose: true,
      autoFocus: false
    });

    ref.afterClosed().subscribe((result) => {
      if (result) {
        this.toastr.success('Legal information shared successfully.');
        this.load();
      }
    });
  }

  getSharedWith(entry: PreReadListItem): any[] {
    // The list response already includes shared_with per entry (same field
    // hasActiveSharedAccess() reads for the delete-confirmation dialog), so
    // no separate per-row /shared-with call is needed here.
    return entry.shared_with || [];
  }

  // ---------- Custom pagination (same pattern as documents.component.ts) ----------

  getTotalPages(): number {
    return Math.ceil((this.filteredEntries?.length || 0) / this.pageSize);
  }

  getStartIndex(): number {
    return this.currentPage * this.pageSize;
  }

  getEndIndex(): number {
    const end = (this.currentPage + 1) * this.pageSize;
    const total = this.filteredEntries?.length || 0;
    return Math.min(end, total);
  }

  goToFirstPage(): void {
    if (this.currentPage !== 0) { this.currentPage = 0; }
  }

  goToLastPage(): void {
    const lastPage = this.getTotalPages() - 1;
    if (this.currentPage !== lastPage) { this.currentPage = lastPage; }
  }

  goToPreviousPage(): void {
    if (this.currentPage > 0) { this.currentPage--; }
  }

  goToNextPage(): void {
    if (this.currentPage < this.getTotalPages() - 1) { this.currentPage++; }
  }

  goToPage(page: number): void {
    if (page >= 0 && page < this.getTotalPages()) { this.currentPage = page; }
  }

  getVisiblePages(): number[] {
    const totalPages = this.getTotalPages();
    const current = this.currentPage;
    const pages: number[] = [];

    if (totalPages <= 7) {
      for (let i = 0; i < totalPages; i++) { pages.push(i); }
    } else {
      pages.push(0);
      if (current <= 3) {
        for (let i = 1; i <= 4; i++) { pages.push(i); }
        pages.push(-1);
        pages.push(totalPages - 1);
      } else if (current >= totalPages - 4) {
        pages.push(-1);
        for (let i = totalPages - 5; i < totalPages; i++) { pages.push(i); }
      } else {
        pages.push(-1);
        for (let i = current - 1; i <= current + 1; i++) { pages.push(i); }
        pages.push(-1);
        pages.push(totalPages - 1);
      }
    }
    return pages;
  }
}